import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { signupStep1, validateEmailOTP } from "../api/Api";
import { ACCESS_TOKEN, ADMIN_TOKEN } from "../config/Config";
import { useAuth } from "../contexts/AuthContext";

const Signup = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [otpPageEnabled, setOtpPageEnabled] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    password: "",
    orgName: "education",
    otp: "",
    channelName: "",
    ...(location.state || {})
  });

  useEffect(() => {
    document.title = "Signup";
    // Set mobile as password by default
    setFormData(prev => ({ ...prev, password: prev.mobile }));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'mobile' && { password: value }) // Update password when mobile changes
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.mobile) {
      return toast.error("Please fill all required fields");
    }

    try {
      const res = await signupStep1(formData);
      
      if (res.message === "Already signed up!! please click login.") {
        toast.success("Already signed up!! please click login.");
        navigate("/signin");
      } else {
        toast.success(
          `Hello ${formData.name}, please check your email (${formData.email}) for the OTP`
        );
        setOtpPageEnabled(true);
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong. Please try again!");
      console.error(error);
    }
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.otp) {
      return toast.error("Please enter the OTP");
    }

    try {
      const otpValidationRequest = {
        email: formData.email,
        otp: formData.otp,
        mobileAsPassword: formData.mobile
      };

      const res = await validateEmailOTP(otpValidationRequest);
      localStorage.setItem(ACCESS_TOKEN, res.accessToken || null);
      
      // Handle token expiration
      const token = res.accessToken;
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const decodedToken = JSON.parse(window.atob(base64));
      
      if (decodedToken.exp <= Math.floor(Date.now() / 1000)) {
        localStorage.removeItem(ACCESS_TOKEN);
        localStorage.removeItem(ADMIN_TOKEN);
        return toast.error("Session expired. Please try again.");
      }

      // Login the user
      login(token, decodedToken.rol?.[0]);
      toast.success("Signup successful!");
      navigate("/");
    } catch (error) {
      toast.error(error.message || "Invalid OTP. Please try again!");
      console.error(error);
    }
  };

  return (
    <div className="md:mt-28">
      <div className="bg-gray-100 py-12">
        <h2 className="text-3xl text-blue-900 uppercase font-bold text-center mb-4">
          Signup
        </h2>
        
        {otpPageEnabled ? (
          <OTPForm 
            formData={formData} 
            handleChange={handleChange} 
            handleSubmit={handleOTPSubmit} 
          />
        ) : (
          <SignupForm 
            formData={formData} 
            handleChange={handleChange} 
            handleSubmit={handleSubmit} 
          />
        )}
      </div>
      
      <Toaster
        position="bottom-right"
        toastOptions={{
          error: {
            duration: 6000,
            style: { background: "#FF4136", color: "#fff" }
          },
          success: {
            duration: 6000,
            style: { background: "#008000", color: "#fff" }
          }
        }}
      />
    </div>
  );
};

// Sub-components for better organization
const SignupForm = ({ formData, handleChange, handleSubmit }) => (
  <>
    <form
      className="max-w-md mx-auto bg-white p-6 rounded-lg shadow-md"
      onSubmit={handleSubmit}
    >
      <FormField
        label="Name"
        id="name"
        name="name"
        type="text"
        placeholder="Enter your name"
        value={formData.name}
        onChange={handleChange}
        required
      />
      
      <FormField
        label="Email"
        id="email"
        name="email"
        type="email"
        placeholder="Enter your email"
        value={formData.email}
        onChange={handleChange}
        required
      />
      
      <FormField
        label="Mobile No"
        id="mobile"
        name="mobile"
        type="tel"
        placeholder="Enter your mobile number"
        value={formData.mobile}
        onChange={handleChange}
        required
      />
      
      <button
        type="submit"
        className="bg-blue-900 hover:bg-blue-700 text-white py-2 px-4 rounded w-full mt-4"
      >
        Sign Up
      </button>
    </form>
    
    <p className="text-center mt-4">
      Already have an account?{" "}
      <Link to="/login" className="text-blue-900 underline">
        Log in here
      </Link>
    </p>
  </>
);

const OTPForm = ({ formData, handleChange, handleSubmit }) => (
  <form
    onSubmit={handleSubmit}
    className="bg-white max-w-md mx-auto p-6 rounded-lg shadow-md"
  >
    <FormField
      label="Enter OTP:"
      id="otp"
      name="otp"
      type="text"
      value={formData.otp}
      onChange={handleChange}
      required
    />
    
    <button
      type="submit"
      className="w-full bg-blue-500 text-white py-2 rounded hover:bg-blue-600"
    >
      Verify OTP
    </button>
  </form>
);

const FormField = ({ label, id, name, type, placeholder, value, onChange, required }) => (
  <div className="mb-4">
    <label htmlFor={id} className="block text-gray-700">
      {label}
    </label>
    <input
      id={id}
      name={name}
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className="border border-gray-300 rounded-md w-full p-2 mt-1"
      required={required}
    />
  </div>
);

export default Signup;